import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ExternalLink, Loader2, Plus, X } from 'lucide-react';
import { agentsApi } from '../../services/agentsApi';
import { splitLine, totalLine, type DepartmentCounts } from './departmentCounts';

/**
 * The departments card's real half: which departments exist, and how many
 * people are in each.
 *
 * The section C rows above hold what the company TOLD us (how many departments,
 * their names, the org chart link). This shows what is actually on the team, with
 * the seniority split B3 added — because "Operations, 12 people, 2 leadership"
 * is the sentence from the call, and a count of names is not.
 *
 * Deliberately no list of individual people. Josh was explicit that seventy
 * names on a card is not the goal; that is what the Team page is for.
 */

interface DepartmentRow extends DepartmentCounts {
  id: string;
  name: string;
  archived?: boolean;
}

export interface DepartmentsPanelProps {
  clientId?: string;
  /** Org chart URL from c.org_chart_url, shown as a link when we have one. */
  orgChartUrl?: string | null;
}

export const DepartmentsPanel: React.FC<DepartmentsPanelProps> = ({ clientId, orgChartUrl }) => {
  const [rows, setRows] = useState<DepartmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    try {
      const res = await agentsApi.listWorkspaceDepartments(clientId);
      setRows((res.data?.departments ?? []) as DepartmentRow[]);
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    const value = name.trim();
    if (!value || !clientId) return;
    setBusy(true);
    setError(null);
    try {
      await agentsApi.createWorkspaceDepartment(clientId, value);
      setName('');
      setAdding(false);
      await load();
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setBusy(false);
    }
  };

  const live = rows.filter(r => !r.archived);

  return (
    <div>
      <span className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        Departments on your team
      </span>

      {loading ? (
        <div className="h-14 animate-pulse rounded-xl bg-gray-100" />
      ) : live.length === 0 ? (
        <p className="text-[12.5px] leading-relaxed text-gray-500">
          No departments yet. Add the ones your company is organised into — people
          get assigned to them when your team is invited or uploaded.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {live.map(d => {
            const split = splitLine(d);
            return (
              <li
                key={d.id}
                className="flex items-baseline justify-between gap-3 rounded-xl bg-white px-3 py-2 ring-1 ring-gray-200"
              >
                <span className="min-w-0 text-[13.5px] font-medium text-gray-900">{d.name}</span>
                <span className="flex flex-none flex-col items-end">
                  <span className="text-[12px] text-gray-600">{totalLine(d)}</span>
                  {split && <span className="text-[11px] text-gray-400">{split}</span>}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <p role="alert" className="mt-2.5 inline-flex items-start gap-1.5 text-[11.5px] font-medium text-red-600">
          <AlertCircle size={12} strokeWidth={2.5} className="mt-px flex-none" />
          {error}
        </p>
      )}

      {orgChartUrl && (
        <a
          href={orgChartUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2.5 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-brand-600 hover:underline"
        >
          <ExternalLink size={12} strokeWidth={2.25} />
          Open your org chart
        </a>
      )}

      <div className="mt-3">
        {adding ? (
          <div className="rounded-xl bg-brand-50/50 p-3 ring-1 ring-brand-100">
            <div className="flex items-center gap-2">
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') void add(); }}
                autoFocus
                placeholder="Loan Operations"
                aria-label="Department name"
                className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-[13px] text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              <button
                type="button"
                disabled={!name.trim() || busy}
                onClick={() => { void add(); }}
                className="inline-flex flex-none items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-500 disabled:cursor-not-allowed disabled:bg-gray-300 whitespace-nowrap"
              >
                {busy && <Loader2 size={12} strokeWidth={2.5} className="animate-spin" />}
                Add
              </button>
              <button
                type="button"
                onClick={() => { setAdding(false); setName(''); }}
                className="flex-none rounded-md p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                aria-label="Cancel"
              >
                <X size={13} strokeWidth={2.25} />
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-brand-600 whitespace-nowrap"
          >
            <Plus size={13} strokeWidth={2.5} />
            Add a department we missed
          </button>
        )}
      </div>
    </div>
  );
};

function messageFor(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (e instanceof Error && e.message) return e.message;
  return "Couldn't load your departments.";
}

export default DepartmentsPanel;
